import { useEffect, useRef } from 'react';

// Right-side slide-in drawer. Stays mounted so it can animate both directions;
// `open` toggles the is-open class. Same prop shape as Modal (title/onClose/footer).
//
// Accessibility: when closed we mark the root `inert` (not aria-hidden). `inert` both hides the
// subtree from assistive tech AND removes it from the focus order — so a button inside can't keep
// focus while the container is hidden (the cause of the "aria-hidden on a focused ancestor" warning).
export default function Drawer({ open, title, onClose, children, footer, width = 540 }) {
  const panelRef = useRef(null);
  const opener = useRef(null);
  // Keep the latest onClose without making it an effect dependency (an inline onClose from the
  // parent changes identity every render; depending on it would re-run the focus effect on every
  // keystroke and steal focus out of the field being typed in).
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    if (!open) return;
    // Remember what had focus so we can restore it when the drawer closes.
    opener.current = document.activeElement;
    const t = setTimeout(() => {
      const panel = panelRef.current;
      // Only pull focus to the panel if nothing inside it is already focused (respects autoFocus).
      if (panel && !panel.contains(document.activeElement)) panel.focus();
    }, 0);

    function onKey(e) { if (e.key === 'Escape') onCloseRef.current?.(); }
    document.addEventListener('keydown', onKey);

    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      // Return focus to whatever opened the drawer (no-op if it's gone).
      const el = opener.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [open]); // only on open/close transitions — NOT on every parent re-render

  return (
    <div className={`drawer-root${open ? ' is-open' : ''}`} inert={!open}>
      <div className="drawer__overlay" onMouseDown={onClose} />
      <aside
        className="drawer__panel"
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className="drawer__head">
          <h2 className="drawer__title">{title}</h2>
          <button className="drawer__close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="drawer__body">{open ? children : null}</div>
        {footer && <div className="drawer__foot">{footer}</div>}
      </aside>
    </div>
  );
}
